import { z } from 'zod';

/**
 * VENDORED from ctd-web-api `src/api-cms/analytics/envelope/kpi-envelope.schema.ts`,
 * the canonical KPI envelope contract. This is a hand-kept copy: the backend pins
 * ITS copy to the OpenAPI `KpiEnvelope` schema in CI, but nothing pins THIS copy to
 * the backend, so the two can diverge until someone re-syncs by hand. A shared
 * package would remove the hand-copy and close that gap; until then, re-copy this
 * file whenever the backend schema changes.
 *
 * The CMS uses it to validate envelopes at the network boundary and to derive the
 * envelope types (see `kpi-envelope.ts`), so the frontend has one definition that
 * is the shape, the runtime validator, and the types at once.
 *
 * Re-synced against API PR #697: `capabilities` now carries the required
 * `regionGrain` enum (kept in sync with the API); the deprecated `provinces`
 * boolean has been dropped, matching the API's removal of the field.
 *
 * Re-synced again for the breakdown-axis contract: `envelopeBreakdownSchema` is
 * discriminated on `by` and pins a row-key set per axis, and the never-emitted
 * singular `breakdown` field has been dropped. Unlike the API copy, the
 * breakdowns array here is tolerant per entry (see `envelopeBreakdownsSchema`):
 * the CMS validates at a network boundary, so an axis it does not model is
 * dropped rather than failing the whole envelope.
 *
 * And re-synced for the app-downloads envelope split: the optional
 * `dataCompleteThrough` date was added to the contract. It has to be modelled
 * here to be readable at all, since this schema strips keys it does not know.
 *
 * Re-synced for the region-axis contract: the brand axis is `by: 'brand'`, not
 * `by: 'appTarget'` (that name meant the `CA`/`QC` app selector everywhere else
 * in the CMS while this axis carried `CT`/`CK`), and `brand` is now a region axis
 * bounded by `regionGrain` like the other two, publishable by every grain that
 * resolves geography.
 *
 * Re-synced against API PR #765: a breakdown row may now carry its own
 * per-day `series` (optional, `granularity=daily` only), so a region axis can
 * back a chart, not just a bar/pie/funnel. Mirrors the top-level `series`
 * field, one level down.
 *
 * Re-synced for the age-brackets endpoint: `ageBracket` is a closed axis like
 * `device`, not a region axis, with the seven fixed keys the age-brackets
 * service publishes (`AGE_BRACKET_KEYS`, Unknown last), added as its own new
 * branch below. Its rows also carry `rate`/`base` (the bracket's share of the
 * cohort scalar), the same way the `promotion` axis's top-redeemed rows do:
 * unlike the axis branch itself, the rate/base cells needed no schema change,
 * since `breakdownRow`'s catchall already keeps any numeric cell through
 * `modeledCells`.
 *
 * Re-synced for campaign-milestones-member: `visitMilestone` is a closed axis
 * like `ageBracket`, with the fifteen fixed ordinal keys the visit-milestone
 * ladder publishes (`VISIT_MILESTONE_KEYS`). Not a region axis.
 *
 * Re-synced against API PR #880: `couponGroup` joins the open-key axes, one
 * row per coupon as a reader means it, keyed `<tierRewardId>:<activationCode>`
 * or, for a coupon with no activation code, `<tierRewardId>:promotion-<id>`,
 * falling back to a bare `<promotionId>` where no live tier places it.
 */

export const dateRangeSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
});

/** Compare-period value for a `scalar` metric; the frontend computes the delta. */
export const scalarCompareSchema = z.object({
    value: z.number(),
});

/** Compare-period values for a `ratio` metric; `rate` is null when denominator is 0. */
export const ratioCompareSchema = z.object({
    numerator: z.number(),
    denominator: z.number(),
    rate: z.number().nullable(),
});

/** A single-value metric (e.g. `newUsers`). `value` is nullable: `null` means
 * the emitting KPI declared the metric unavailable for this window. An absent
 * `value` key is unchanged by this: still a validation error, not a way to
 * express unavailability. */
export const scalarMetricSchema = z.object({
    type: z.literal('scalar'),
    value: z.number().nullable(),
    compare: scalarCompareSchema.optional(),
});

export const ratioMetricSchema = z.object({
    type: z.literal('ratio'),
    numerator: z.number(),
    denominator: z.number(),
    rate: z.number().nullable(),
    compare: ratioCompareSchema.optional(),
});

/** Discriminated on `type`, matching the `EnvelopeMetric` union. */
export const envelopeMetricSchema = z.discriminatedUnion('type', [
    scalarMetricSchema,
    ratioMetricSchema,
]);

/**
 * The metric types this vendored copy models. Kept as one list so the tolerance
 * logic below and the strict schemas above (`scalarMetricSchema`/`ratioMetricSchema`)
 * stay obviously in sync; add a new member here whenever a metric type is added.
 */
const KNOWN_METRIC_TYPES = ['scalar', 'ratio'] as const;

/** True when a raw value claims a known metric type, so it must match that type's strict shape. */
const claimsKnownMetricType = (value: unknown): boolean => {
    if (typeof value !== 'object' || value === null) return false;
    const { type } = value as { type?: unknown };
    return KNOWN_METRIC_TYPES.some((known) => known === type);
};

/**
 * Envelope metrics, keyed by metric id, with per-entry tolerance (same as the
 * series/breakdown cells below): each value is parsed on its own, so one bad
 * entry never rejects the whole envelope.
 *
 * - A well-formed known metric is kept.
 * - A value that *claims* a known type (`scalar`/`ratio`) but fails its strict
 *   shape is a real contract violation, so it fails the whole envelope loudly
 *   rather than being hidden.
 * - Anything else (an unmodeled type, or a malformed entry with no/`non-string`
 *   type, or a non-object) is tolerated and dropped at the boundary.
 *
 * A dropped metric is simply absent, so only the card that reads it goes to error
 * state (see create-envelope-source) while every other card on the endpoint still
 * renders.
 */
const envelopeMetricsSchema = z
    .record(z.string(), z.unknown())
    .transform((raw, ctx) => {
        const modeled: [string, z.infer<typeof envelopeMetricSchema>][] = [];

        for (const [id, value] of Object.entries(raw)) {
            const parsed = envelopeMetricSchema.safeParse(value);
            if (parsed.success) {
                modeled.push([id, parsed.data]);
                continue;
            }
            if (claimsKnownMetricType(value)) {
                parsed.error.issues.forEach((issue) =>
                    ctx.addIssue({ ...issue, path: [id, ...issue.path] })
                );
                return z.NEVER;
            }
            // Unknown/garbage entry: dropped (not pushed to `modeled`).
        }

        return Object.fromEntries(modeled);
    });

/** A metric value inside a series point or breakdown row (keyed by metric id). */
const metricCellSchema = z.union([z.number(), z.string(), z.null()]);

type MetricCell = z.infer<typeof metricCellSchema>;

/**
 * Keep only cells whose value matches `metricCellSchema`; drop the rest. Same
 * per-entry tolerance the metrics record got: an unmodeled/invalid cell (e.g. a
 * nested object shipped by a drifted backend) is dropped at the boundary instead
 * of failing the whole envelope. A dropped cell reads back as absent, so only the
 * card on that metric degrades (flat/zero for that point) while the rest render.
 */
const modeledCells = (
    cells: Record<string, unknown>
): Record<string, MetricCell> =>
    Object.fromEntries(
        Object.entries(cells).filter(
            (entry): entry is [string, MetricCell] =>
                metricCellSchema.safeParse(entry[1]).success
        )
    );

/** A per-day point: fixed `date` plus dynamic metric-id keys (tolerant per cell). */
export const envelopeSeriesPointSchema = z
    .object({ date: z.string() })
    .catchall(z.unknown())
    .transform(({ date, ...cells }): { date: string } & Record<
        string,
        MetricCell
    > => ({
        date,
        ...modeledCells(cells),
    }));

/**
 * Row key domain, one `*_KEYS` tuple per closed axis, all declared the same way
 * so `z.enum(…_KEYS)` reads identically for every axis below. Vendored with the
 * rest of this file: the API sources the province list from `provinceHelpers`,
 * the CMS has no such list of its own.
 *
 * `province` and `businessUnit` carry an `UNKNOWN` bucket, so records with no
 * province on file still have a row and the rows keep summing to the scalar.
 *
 * `BRAND_KEYS` are the retail banners (`CT`/`CK`), not the request-side app
 * targets (`CA`/`QC`): a `key: 'QC'` on a region axis was indistinguishable from
 * the province code.
 */
const PROVINCE_KEYS = [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
    'UNKNOWN',
] as const;
const BUSINESS_UNIT_KEYS = [
    'eastern_canada',
    'atlantic',
    'ontario',
    'western_canada',
    'UNKNOWN',
] as const;
const BRAND_KEYS = ['CT', 'CK'] as const;
const DEVICE_KEYS = ['ios', 'android'] as const;
/** Fixed age order the axis publishes, Unknown last. Must match
 * AGE_BRACKET_KEYS in ctd-web-api's own kpi-envelope.schema.ts. */
const AGE_BRACKET_KEYS = [
    'under-18',
    '18-24',
    '25-34',
    '35-49',
    '50-64',
    '65-plus',
    'unknown',
] as const;
/** Fixed domain of the campaign cohort axis: no `UNKNOWN`, `existing + new` is
 * the total. Must match COHORT_KEYS in ctd-web-api's own kpi-envelope.schema.ts. */
const COHORT_KEYS = ['existing', 'new'] as const;
/** Fixed fifteen-ordinal domain the visit-milestone ladder publishes. Must
 * match VISIT_MILESTONE_KEYS in ctd-web-api's own kpi-envelope.schema.ts. */
export const VISIT_MILESTONE_KEYS = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
] as const;

export const COIN_COUNT_KEYS = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
] as const;
export const ENTRY_TIER_KEYS = ['1', '2', '3'] as const;

/** The shape every breakdown row shares, with `key` left as a bare string. Kept
 * non-generic on purpose: zod widens an object's output to `… | undefined` when
 * a generic type parameter is involved, which forces the row `key` to be
 * narrowed by a hand-written cast. Parsing the common shape here and applying
 * the per-axis key schema in `breakdownRow` keeps that narrowing runtime-proven.
 *
 * `series` is a declared key (not a `catchall` cell) so it parses as an actual
 * array of points rather than a tolerated/dropped cell: `metricCellSchema` only
 * admits `number | string | null`, so an array there would fail the cell check
 * and vanish silently. Optional, like the top-level `series`: the API emits it
 * only per row on `granularity=daily`, per axis (see `age-gating-acquisition`'s
 * `businessUnit` breakdown), and a required key here would reject every
 * envelope from every OTHER KPI's rows, which never carry it. */
const breakdownRowBase = z
    .object({
        key: z.string(),
        label: z.string(),
        series: z.array(envelopeSeriesPointSchema).optional(),
    })
    .catchall(z.unknown());

/** A breakdown row: fixed `key`/`label`/optional `series` plus dynamic metric-id
 * keys (tolerant per cell). The `key` schema is supplied per axis and is applied
 * to the parsed key, so `key` is narrowed to the axis domain by an actual parse
 * rather than by assertion. Key issues are re-emitted under the `key` path, so a
 * bad row still reports as `…rows[0].key` with the axis schema's own message. */
function breakdownRow<Key extends string>(keySchema: z.ZodType<Key, Key>) {
    return breakdownRowBase.transform(({ key, label, series, ...cells }, ctx): {
        key: Key;
        label: string;
        series?: z.infer<typeof envelopeSeriesPointSchema>[];
    } & Record<string, MetricCell> => {
        const parsedKey = keySchema.safeParse(key);
        if (!parsedKey.success) {
            for (const issue of parsedKey.error.issues) {
                ctx.addIssue({ ...issue, path: ['key', ...issue.path] });
            }
            return z.NEVER;
        }
        // `as unknown as`, not a plain return: `series` (an array) and the
        // `Record<string, MetricCell>` catchall can never both hold for every
        // key of one literal TS checks structurally (that check would demand
        // `series` itself satisfy `MetricCell`). Same conflict, same escape
        // hatch the API side takes on this exact shape (`age-gating-
        // acquisition.service.ts`'s `as unknown as BusinessUnitBreakdownRow`).
        return ({
            key: parsedKey.data,
            label,
            ...(series !== undefined && { series }),
            ...modeledCells(cells),
        } as unknown) as {
            key: Key;
            label: string;
            series?: z.infer<typeof envelopeSeriesPointSchema>[];
        } & Record<string, MetricCell>;
    });
}

/** A row on an open-key axis (`round`, `badge`, `bonusAction`, `promotion`,
 * `couponGroup`, `category`): `key` is a runtime id with no finite domain,
 * or, on `couponGroup`, a composite of one. */
export const envelopeBreakdownRowSchema = breakdownRow(z.string());

export const provinceBreakdownRowSchema = breakdownRow(z.enum(PROVINCE_KEYS));
export const businessUnitBreakdownRowSchema = breakdownRow(
    z.enum(BUSINESS_UNIT_KEYS)
);
export const brandBreakdownRowSchema = breakdownRow(z.enum(BRAND_KEYS));
export const deviceBreakdownRowSchema = breakdownRow(z.enum(DEVICE_KEYS));
export const ageBracketBreakdownRowSchema = breakdownRow(
    z.enum(AGE_BRACKET_KEYS)
);
export const cohortBreakdownRowSchema = breakdownRow(z.enum(COHORT_KEYS));
export const visitMilestoneBreakdownRowSchema = breakdownRow(
    z.enum(VISIT_MILESTONE_KEYS)
);
export const coinCountBreakdownRowSchema = breakdownRow(
    z.enum(COIN_COUNT_KEYS)
);
export const entryTierBreakdownRowSchema = breakdownRow(
    z.enum(ENTRY_TIER_KEYS)
);

/**
 * A dimensional split, discriminated on the axis so each axis pins its own row
 * key set. With `by` and `key` both bare strings, `{ by: 'province', rows: [{
 * key: 'atlantic' }] }` parsed cleanly even though `atlantic` is a business
 * unit, and the brand axis emitted `key: 'QC'` for Couche-Tard,
 * indistinguishable from the province code. Discriminating ties the axis label to
 * the row keys at compile time as well as at parse time.
 *
 * Region axes are a presentation fold, not a data limit: a province-grain KPI
 * may legitimately serve a `businessUnit` breakdown.
 * `capabilities.regionGrain` states the finest resolution the data supports and
 * bounds which region axes an endpoint can offer; it is not implied by the axis
 * emitted here.
 */
export const envelopeBreakdownSchema = z.discriminatedUnion('by', [
    z.object({
        by: z.literal('province'),
        rows: z.array(provinceBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('businessUnit'),
        rows: z.array(businessUnitBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('brand'),
        rows: z.array(brandBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('device'),
        rows: z.array(deviceBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('ageBracket'),
        rows: z.array(ageBracketBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('cohort'),
        rows: z.array(cohortBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('visitMilestone'),
        rows: z.array(visitMilestoneBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('coinCount'),
        rows: z.array(coinCountBreakdownRowSchema),
    }),
    z.object({
        by: z.literal('entryTier'),
        rows: z.array(entryTierBreakdownRowSchema),
    }),
    z.object({
        by: z.enum([
            'round',
            'badge',
            'bonusAction',
            'promotion',
            'rewardOption',
            'couponGroup',
            'category',
            'coin',
        ]),
        rows: z.array(envelopeBreakdownRowSchema),
    }),
]);

/** Every axis the union accepts, derived from the union so this list cannot
 * drift from the branches above. */
export const BREAKDOWN_AXES: readonly string[] = envelopeBreakdownSchema.options.flatMap(
    (option) => {
        const by = option.shape.by;
        return by instanceof z.ZodLiteral
            ? [by.value as string]
            : [...by.options];
    }
);

const KNOWN_BREAKDOWN_AXES = new Set<string>(BREAKDOWN_AXES);

/** Does this entry name an axis this copy of the schema models? */
const claimsKnownAxis = (value: unknown): boolean => {
    const by = (value as { by?: unknown } | null)?.by;
    return typeof by === 'string' && KNOWN_BREAKDOWN_AXES.has(by);
};

/**
 * The breakdowns array, tolerant per entry, mirroring how `envelopeMetricsSchema`
 * treats the metrics record.
 *
 * - A breakdown on a known axis that fails its row keys is a real contract
 *   violation (the mislabelled-axis bug), so it fails the whole envelope loudly.
 * - A breakdown on an axis this copy does not model is dropped at the boundary,
 *   so a backend that ships a new axis before the CMS re-syncs degrades only the
 *   card that asked for it instead of blanking every card on the endpoint.
 */
export const envelopeBreakdownsSchema = z
    .array(z.unknown())
    .transform((raw, ctx) => {
        const modeled: z.infer<typeof envelopeBreakdownSchema>[] = [];

        for (const [index, value] of raw.entries()) {
            const parsed = envelopeBreakdownSchema.safeParse(value);
            if (parsed.success) {
                modeled.push(parsed.data);
                continue;
            }
            if (claimsKnownAxis(value)) {
                parsed.error.issues.forEach((issue) =>
                    ctx.addIssue({ ...issue, path: [index, ...issue.path] })
                );
                return z.NEVER;
            }
            // Unmodeled axis: dropped (not pushed to `modeled`).
        }

        return modeled;
    })
    .default([]);

/**
 * One human-facing note on the envelope, discriminated on `kind`. Mirrors the
 * API's `envelopeCaveatSchema`.
 *
 * A `warning` says something bit THIS response and carries a stable `code`;
 * the API sends no `caveat` now, but rows cached before it stopped still do.
 *
 * The two used to be one `Array<string>`, which is why the Taxi page found the
 * empty-window signal by matching `'(empty period)'` inside the prose, and why
 * the info bubble could not colour on "something is off" without also
 * colouring the 15 KPIs that merely state a standing limit.
 */
export const envelopeCaveatSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('caveat'),
        text: z.string().min(1),
    }),
    z.object({
        kind: z.literal('warning'),
        code: z.string(),
        text: z.string().min(1),
    }),
]);

/** `period_not_covered` covers both a period the source hasn't published yet
 * and one it no longer retains: one cause, not two. */
export const degradationCauseSchema = z.enum([
    'period_not_covered',
    'source_unavailable',
    'region_unsupported',
]);

/** `cause` is optional only until api-cms deploys the new shape; the cleanup
 * PR makes it required. */
export const envelopeDegradationSchema = z.object({
    cause: degradationCauseSchema.optional(),
    reason: z.string(),
});

/** Region grain the endpoint actually resolved: the finest region resolution
 * actually served, which can be coarser than province (e.g. `businessUnit` or
 * `brand`) depending on the KPI (see active-users, engagement, app-downloads,
 * profile-creation-conversion). Required: every envelope KPI service sets its
 * own grain literal (the shared layer stays KPI-agnostic, no central
 * kpi->grain map). */
export const regionGrainSchema = z.enum([
    'national',
    'brand',
    'businessUnit',
    'province',
]);

export const envelopeCapabilitiesSchema = z.object({
    daily: z.boolean(),
    compare: z.boolean(),
    regionGrain: regionGrainSchema,
});

/** Time aggregation for a KPI request: period totals or a per-day series. */
export const granularitySchema = z.enum(['total', 'daily']);

const kpiEnvelopeObjectSchema = z.object({
    kpi: z.string(),
    range: dateRangeSchema,
    /**
     * ISO instant the API computed this envelope's numbers. A cached envelope
     * reports when it was computed, not when it was served. Declared here so
     * the CMS parses what the API sends instead of stripping it; nothing in the
     * UI reads it yet.
     *
     * SYNC NOTE: required in the backend schema, which always knows it. Optional
     * here only so the CMS can ship before the API that emits it; make it
     * required once that API is deployed.
     */
    computedAt: z.string().datetime().optional(),
    /**
     * Last UTC calendar date (`YYYY-MM-DD`) through which the data in THIS
     * envelope is complete. It answers "how far does what you are holding
     * actually go", not "what is the newest date the upstream source knows
     * about": a request ending before the source's own freshness date is
     * complete through its `range.endDate`, and the source knowing more does
     * not change that.
     *
     * Optional, and absent for almost every KPI: only a KPI whose source lags
     * behind the requested period (the per-platform app-downloads KPIs, whose
     * stores report on their own delay) has a boundary to declare. Absent too
     * on an `unavailable` envelope, which holds no complete data at all.
     */
    dataCompleteThrough: z.string().optional(),
    compareRange: dateRangeSchema.nullable(),
    granularity: granularitySchema,
    metrics: envelopeMetricsSchema,
    series: z.array(envelopeSeriesPointSchema).optional(),
    // `breakdowns` are the dimensional axes a card selects via `breakdownBy`,
    // including the ordered funnel a `columns` card renders. There is no
    // singular `breakdown`: this mirror declared one for a while, but no API
    // service ever emitted it and it was absent from both the canonical zod
    // schema and the OpenAPI contract, so it only ever parsed as `undefined`.
    breakdowns: envelopeBreakdownsSchema,
    degradation: envelopeDegradationSchema.optional(),
    capabilities: envelopeCapabilitiesSchema,
    caveats: z.array(envelopeCaveatSchema),
});

/**
 * Region axes whose row keys are bounded by geography, as opposed to
 * `device`/`round`/`badge`/`bonusAction`, which carry no region meaning and
 * are unaffected by `regionGrain` at any value. Mirrors the API's rule (see
 * ctd-web-api `kpi-envelope.schema.ts`).
 */
const REGION_AXES: ReadonlySet<string> = new Set([
    'province',
    'businessUnit',
    'brand',
]);

/**
 * The region axes each grain may publish. A KPI may only publish a region
 * axis at or coarser than the region grain it declares: a `businessUnit`-
 * grain KPI publishing a `province` axis would be claiming a resolution its
 * data does not have, the same class of untruth the discriminated `by` union
 * was introduced to stop.
 */
const REGION_GRAIN_AXES: Record<
    z.infer<typeof regionGrainSchema>,
    ReadonlySet<string>
> = {
    province: new Set(['province', 'businessUnit', 'brand']),
    businessUnit: new Set(['businessUnit', 'brand']),
    brand: new Set(['brand']),
    national: new Set(),
};

/**
 * Cross-field refinement relating `capabilities.regionGrain` to
 * `breakdowns[].by`, mirroring the API's rule. It runs after
 * `envelopeBreakdownsSchema`'s own tolerant-per-entry pass (see above), so an
 * axis this copy does not model is already dropped by the time this check
 * sees `breakdowns` -- it never turns an unmodeled axis into a hard failure.
 * A modeled axis that exceeds the declared grain IS a real contract
 * violation, so it fails the whole envelope loudly, same as a modeled axis
 * with bad row keys already does.
 */
export const kpiEnvelopeSchema = kpiEnvelopeObjectSchema.superRefine(
    (envelope, ctx) => {
        const allowed = REGION_GRAIN_AXES[envelope.capabilities.regionGrain];
        envelope.breakdowns.forEach((breakdown, index) => {
            if (REGION_AXES.has(breakdown.by) && !allowed.has(breakdown.by)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `regionGrain '${envelope.capabilities.regionGrain}' cannot publish the '${breakdown.by}' region axis: finer than the declared grain`,
                    path: ['breakdowns', index, 'by'],
                });
            }
        });
    }
);
